<template>
  <footer
    class="c-bg-black"
    :class="{ 'reduced-footer': isReducedCheckoutFooter }"
  >
    <div class="footer-links m0 row block pt30 pt59-desktop">
      <div class="container mobile-column flex center-md bottom-links mt2 bold">
        <div
          v-if="!isReducedCheckoutFooter"
          class="flex mobile-column row between-md between-xs px5 upper-container"
        >
          <div class="footer-column">
            <newsletter
              block-identifier="newsletter-block"
            />
            <cms-block
              class="site-details fs18 lh21 hidden-mobile hidden-tablet"
              :identifier="'terms-and-conditions'"
              :process="isMobileOrTablet() && isCheckoutPage ? process : null"
            />
          </div>
          <div class="hidden-desktop">
            <footer-dropdown
              :list="firstLevelCategories"
              :name="this.$t('Find tickets for you')"
            />
            <footer-dropdown
              :list="faq_links"
              :name="this.$t('FAQ and contact')"
            />
            <footer-dropdown
              :list="account_links"
              :name="this.$t('My account')"
            />
          </div>
          <div class="footer-column hidden-mobile hidden-tablet">
            <span>{{ $t('Find tickets for you') }}</span>
            <div>
              <router-link
                v-for="(item, index) in categories"
                :key="index"
                :to="categoryLink(item)"
              >
                {{ item.name }}
              </router-link>
            </div>
          </div>
          <div>
            <div class="flex between-md">
              <div class="footer-column hidden-mobile hidden-tablet">
                <span>
                  {{ $t('FAQ and contact') }}
                </span>
                <div>
                  <router-link
                    class="no-underline fs14"
                    :to="localizedRoute('/i/fragor-och-svar')"
                    exact
                  >
                    {{ $t('FAQ') }}
                  </router-link>
                  <router-link
                    :to="localizedRoute('/i/kontakta-oss')"
                    exact
                    class="fs14"
                  >
                    {{ $t('Contact us') }}
                  </router-link>
                  <router-link
                    :to="localizedRoute(giftCardPath)"
                    exact
                    class="fs14"
                  >
                    {{ $t('Gift cards') }}
                  </router-link>
                </div>
              </div>
              <div class="footer-column hidden-mobile hidden-tablet">
                <span>
                  {{ $t('My account') }}
                </span>
                <div>
                  <router-link
                    v-show="isLogged"
                    :to="localizedRoute('/my-account')"
                    exact
                    class="fs14"
                  >
                    {{ $t('My account') }}
                  </router-link>
                  <a
                    v-show="!isLogged"
                    href="#"
                    @click.prevent="goToAccount"
                  >
                    {{ $t('Login') }}
                  </a>
                  <a
                    v-show="!isLogged"
                    href="#"
                    @click.prevent="goToRegisterPage"
                  >
                    {{ $t('Create account') }}
                  </a>
                </div>
              </div>
            </div>
            <div class="information mt24">
              <span>
                {{ $t('Information') }}
              </span>
              <cms-block
                class="mt24"
                :identifier="'footer-information'"
              />
              <a
                data-testid="footerCookieSettingsLink"
                href="#"
                class="fs14"
                @click.prevent="openCookieSettings"
              >
                {{ $t('Cookie settings') }}
              </a>
            </div>
          </div>
        </div>
        <div
          v-if="isReducedCheckoutFooter"
          class="reduced-bar flex"
        >
          <span>
            {{ copyrightText }}
          </span>
          <nav class="reduced-links flex">
            <cms-block
              class="legal-links"
              :identifier="'terms-and-conditions'"
              :process="extractLinks"
            />
            <router-link
              :to="localizedRoute('/i/kontakta-oss')"
              class="contact-link"
              exact
            >
              {{ $t('Contact us') }}
            </router-link>
            <a
              data-testid="footerCookieSettingsLink"
              href="#"
              @click.prevent="openCookieSettings"
            >
              {{ $t('Cookie settings') }}
            </a>
          </nav>
        </div>
        <div
          v-else
          class="pt40 mb40 bottom-container flex col-md-12"
        >
          <cms-block
            class="site-details fs16 lh21 hidden-desktop"
            :identifier="'terms-and-conditions'"
          />
          <span>
            {{ copyrightText }}
          </span>
          <div class="payment-methods flex">
            <span class="hidden-mobile hidden-tablet pr12">{{ $t('Secure payments') }}:</span>
            <div class="images flex">
              <img
                v-for="(item, index) in paymentMethods"
                :key="index"
                :src="item.icon"
                :alt="item.name"
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import { mapGetters } from 'vuex'
import { localizedRoute } from '@vue-storefront/core/lib/multistore'
import CurrentPage from 'theme/mixins/currentPage'
import { getPathForStaticPage } from 'theme/helpers'
import CmsBlock from 'theme/components/core/blocks/Cms/Block'
import FooterDropdown from './FooterDropdown.vue'
import config from 'config'
import Newsletter from './Newsletter.vue'
import Menu from 'theme/mixins/menu'
import { formatCategoryLink } from '@vue-storefront/core/modules/url/helpers'
import { PAGE_PATH as GIFT_CARD_PATH } from 'src/modules/gift-card/constants'
import { MODAL_NAME } from 'src/modules/gdpr-cookie-setup'
import { isReducedFooterVariant } from 'src/modules/ab-test/helpers'

const CMS_LINK_PATTERN = /<a\b[^>]*>[\s\S]*?<\/a>/g

export default {
  name: 'MainFooter',
  components: {
    CmsBlock,
    FooterDropdown,
    Newsletter
  },
  mixins: [CurrentPage, Menu],
  data () {
    return {
      faq_links: [
        { name: this.$t('FAQ'), url_path: this.localizedRoute('/i/fragor-och-svar') },
        { name: this.$t('Contact us'), url_path: this.localizedRoute('/i/kontakta-oss') },
        { name: this.$t('Gift cards'), url_path: this.localizedRoute(GIFT_CARD_PATH) }
      ],
      giftCardPath: GIFT_CARD_PATH,
      paymentMethods: config.paymentMethodIcons.methods,
      isMounted: false
    }
  },
  computed: {
    ...mapGetters({
      isLogged: 'user/isLoggedIn',
      getCategories: 'category-next/getMenuCategories',
      abTestVariant: 'ab-test/getVariant'
    }),
    isReducedCheckoutFooter () {
      return this.isMounted && this.isCheckoutPage && isReducedFooterVariant(this.abTestVariant, config.abTest?.reducedFooterVariants || [])
    },
    categories () {
      const allEventsCategory = this.getCategories.filter((op) => {
        return op.level === (config.entities.category.categoriesDynamicPrefetchLevel >= 0
          ? config.entities.category.categoriesDynamicPrefetchLevel
          : 2)
      })
      return [...allEventsCategory, ...this.firstLevelCategories]
    },
    account_links () {
      const links = [
        {
          name: this.isLogged ? this.$t('My account') : this.$t('Login'),
          url_path: this.localizedRoute('/my-account'),
          isLogged: this.isLogged,
          action: this.goToAccount
        }
      ]

      if (!this.isLogged) {
        links.push({
          name: this.$t('Create account'),
          url_path: '#',
          isLogged: false,
          action: this.goToRegisterPage
        })
      }

      return links
    },
    copyrightText () {
      const currentYear = new Date().getFullYear()
      return this.$t('Copyright {year}', { year: currentYear })
    }
  },
  mounted () {
    this.isMounted = true
  },
  methods: {
    goToAccount () {
      this.$store.commit('ui/setAuthElem', 'login')
      this.$bus.$emit('modal-toggle', 'modal-signup')
    },
    isMobileOrTablet () {
      return typeof window !== 'undefined' ? window?.innerWidth < 1024 : false
    },
    process (string) {
      // Remove first paragraph from the block
      return string.replace(/.+?(<\/p>)/, '')
    },
    /**
     * @param {string} content
     * @return {string}
     */
    extractLinks (content) {
      return (content.match(CMS_LINK_PATTERN) || []).join('')
    },
    goToRegisterPage () {
      this.$store.commit('ui/setAuthElem', 'register')
      this.$bus.$emit('modal-show', 'modal-signup')
    },
    categoryLink (category) {
      return formatCategoryLink(category)
    },
    /**
     * @return {void}
     */
    openCookieSettings () {
      this.$bus.$emit('modal-show', MODAL_NAME)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';
@import '~theme/css/variables/grid';

@media (max-width: 1125px) {
  footer {
    .hidden-desktop {
      display: block;
    }

    .container {
      flex-direction: column;
      padding-left: 20px;
      padding-right: 20px;

      .upper-container {
        flex-direction: column;
      }
    }

    .bottom-container {
      flex-direction: column;
      align-items: center;
      margin-top: 40px;
      margin-bottom: 32px;

      span {
        margin-top: 20px;
        margin-bottom: 20px;
        font-size: 16px;
        line-height: 20px;
      }

      .images {
        height: 28px;
      }
    }

    .site-details {
      text-align: center;
      padding: 0;
      color: #fff;

      /deep/ p {
        font-size: 16px;
      }

      /deep/ a {
        font-size: 16px;
      }
    }

    .information {
      max-width: none;

      /deep/ p {
        line-height: 20px;
        font-size: 16px;
      }
    }
  }

  .hidden-tablet {
    display: none !important;
  }
}

@include mobile {
  footer {
    .footer-links {
      padding-bottom: 100px;
    }

    .container {
      padding-left: 20px;
      padding-right: 20px;
    }

    .hidden-mobile {
      display: none !important;
    }

    .bottom-container {
      flex-direction: column;
      align-items: center;
      margin-top: 40px;
      margin-bottom: 32px;

      span {
        margin-top: 20px;
        margin-bottom: 20px;
        font-size: 16px;
        line-height: 20px;
      }

      .images {
        height: 28px;
      }
    }

    .site-details {
      text-align: center;
      padding: 0;
      color: #fff;

      /deep/ p {
        font-size: 16px;
      }

      /deep/ a {
        font-size: 16px;
      }
    }
  }
}

@include desktop {
  footer {
    /deep/ a {
      &:hover {
        color: color(primary);
      }
    }
  }

  .bottom-links {
    flex-direction: column;
  }

  .upper-container {
    flex-wrap: nowrap;
    gap: 14px;
  }

  .footer-column {
    max-width: 575px;

    span {
      white-space: nowrap;
    }

    .site-details {
      margin-top: auto;

      /deep/ a,
      /deep/ p {
        color: color(white);
        font-size: 20px;
        line-height: 24px;
      }

      /deep/ p {
        margin-bottom: 0;
      }

      /deep/ a {
        &:hover {
          color: color(primary);
        }
      }
    }
  }

  .checkout-footer {
    display: flex;
  }

  .information {
    max-width: 400px;

    /deep/ p {
      line-height: 22px;
      font-size: 18px;
    }
  }
}

.site-details {
  padding-top: 12px;
  line-height: 22px;
  font-weight: 400;
  text-align: left;

  /deep/ p {
    margin: 0;
  }
}

.information {
  text-align: left;

  span {
    font-size: 22px;
    line-height: 32px;
  }

  /deep/ p {
    margin: 0;
    font-weight: 400;
    color: #ddd;
  }
}

.footer-column {
  display: flex;
  flex-direction: column;
  align-items: start;

  span {
    font-size: 22px;
    font-weight: 700;
    line-height: 32px;
    text-align: left;
    margin-bottom: 24px;
  }

  div {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  /deep/ a {
    text-align: left;
    font-size: 22px;
    font-weight: 400;
    line-height: 32px;
    color: #ddd;
  }
}

.newsletter {
  width: 100%;
  max-width: 575px;
  max-height: 185px;
}

.bottom-container {
  margin-top: 80px;
  border-top: solid 1px #626262;
  justify-content: space-between;
  align-content: center;
  color: #ddd;
  line-height: 32px;

  /deep/ span {
    font-size: 18px;
    font-weight: 400;
    font-family: 'Inter', sans-serif;
  }
}

.footer-links {
  color: #fff;
  padding-bottom: 140px;
}

.reduced-footer {
  .footer-links {
    padding-top: 24px;
    padding-bottom: 24px;
  }

  .reduced-bar {
    width: 100%;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;

    > span {
      font-family: 'Inter', sans-serif;
    }
  }

  .reduced-links {
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 20px;
  }

  .legal-links,
  .legal-links /deep/ .parsed-cms-data,
  .legal-links /deep/ div {
    display: contents;
  }

  .reduced-links a,
  .legal-links /deep/ .parsed-cms-data a:not(.button):not(.link):not(.no-underline) {
    color: color(white);
    font-size: 14px;
    line-height: 20px;
    text-decoration: underline;
    white-space: nowrap;

    &:visited {
      color: color(white);
    }

    &:hover {
      color: color(primary);
    }
  }

  .legal-links /deep/ .parsed-cms-data a {
    text-transform: capitalize;
  }

  @media (max-width: 1125px) {
    .reduced-bar {
      flex-direction: column;
    }

    .reduced-links {
      justify-content: center;
    }
  }

  @include mobile {
    .footer-links {
      padding-bottom: 100px;
    }
  }
}

.images {
  gap: 8px;
  max-height: 32px;
}

.bg-white {
  width: 47;
  height: 32;
  background-color: #fff;
}
</style>
